<script lang="ts" setup>
import type { GameItemExtend, CasinoGameItemExtend } from '~/types/game'
import { IMAGE_GAME_ITEM_DEFAULT } from '~/constants'
import { STATUS_LABEL } from '~/resources'
import { useCustomFavoriteGame } from '~/composables/account/use-custom-favorite-game'
import { useGameSpecificEvents } from '~/composables/tracking-events/use-game-specific-events'
import { useUserBehaviorEvents } from '~/composables/tracking-events/use-user-behavior-events'

const { trackingButtonClick } = useUserBehaviorEvents()
const props = defineProps({
  item: {
    type: Object as () => GameItemExtend | CasinoGameItemExtend,
    required: true,
  },
  isLiveCasinoIndirect: {
    type: Boolean,
    default: false,
  },
  isCasino: {
    type: Boolean,
    default: false,
  },
  casinoGameLimit: {
    type: Number,
    default: 10,
  },
  customClass: {
    type: String,
    default: '',
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
  isHideJackpotNumber: {
    type: Boolean,
    default: false,
  },
  isHideEffectHover: {
    type: Boolean,
    default: false,
  },
  isHideLabel: {
    type: Boolean,
    default: false,
  },
})

const route = useRoute()
const { isLogged } = useUser()
const { trackingGameStarted } = useGameSpecificEvents()
const { isMobile } = useDevice()
const { onPlayGame } = usePlayGame()
const { getJackpotNumberByGameId } = useJackpot()
const { isCheckFavorite, handleFavoriteGames } = useCustomFavoriteGame()

const { playCasinoGame } = useCasino({
  gameLimit: props.casinoGameLimit,
})

const handlePlayingGame = (
  item: GameItemExtend | CasinoGameItemExtend,
): void => {
  trackingButtonClick({
    buttonId: `play-game-id-${item.gameId}`,
    buttonName: 'play-game-home',
    pageUrl: route.fullPath,
  })

  if (props.isComingSoon) {
    return
  }

  if (props.isCasino) {
    playCasinoGame(item as CasinoGameItemExtend)
  } else {
    onPlayGame(item)
  }

  if (isLogged.value) {
    trackingGameStarted({
      gameId: item.gameId,
      gameType: item.category?.[0] ?? item.gameType ?? '',
      gameName: item.title,
    })
  }
}

const jackpot = computed(() =>
  getJackpotNumberByGameId(props.item?.gameId ?? ''),
)
</script>
<template>
  <div :class="[customClass, 'game-item']">
    <div class="game-item__image ratio" @click="handlePlayingGame(item)">
      <BaseImage
        :src="item?.img?.thumbnail?.url?.trim()"
        :image-default="IMAGE_GAME_ITEM_DEFAULT"
        :alt="item?.title"
      />
      <div v-if="item?.range" class="game-item__range">
        <span class="line-limit-1">{{ item?.range }}</span>
      </div>
      <div
        v-if="jackpot && !isCasino && !isHideJackpotNumber && item?.jackpot"
        class="game-item__range"
      >
        <BaseAnimatedNumber :number="jackpot" :start="jackpot" />
      </div>
    </div>

    <div
      v-if="!isMobile && !isHideEffectHover"
      class="game-item__thumb"
      @click="handlePlayingGame(item)"
    >
      <div class="game-item__play">
        <span class="icon-flash" />
        Chơi ngay
      </div>
      <div v-if="item.title" class="game-item__game-name line-limit-2">
        {{ item.title }}
      </div>
    </div>

    <template v-for="(status, key) in STATUS_LABEL">
      <div
        v-if="item.group?.includes(status.value)"
        :key="key"
        class="game-item__badge"
      >
        <div
          v-if="!isHideLabel"
          :class="[
            'game-item__status-wrapper',
            `game-item__badge--${status.value}`,
          ]"
        >
          <span v-if="status.icon" :class="status.icon" />
          <span v-if="status.label" class="game-item__label">{{
            status.label
          }}</span>
        </div>
      </div>
    </template>
    <div
      :class="[
        'game-item__favorite',
        {
          'game-item__favorite--active':
            isCheckFavorite(item) || item?.favorite,
        },
        { 'game-item__favorite--mobile': isMobile },
      ]"
      @click="handleFavoriteGames(item)"
    />
  </div>
</template>
<style scoped lang="scss">
@import '~/assets/styles/components/common/game-item.scss';
@import '~/assets/styles/components/responsive/components/common/game-item.scss';
</style>
